import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowRight, Calendar } from 'lucide-react';
import Navbar from '../components/Navbar.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { supabase } from '../lib/supabase';

const HERO_IMAGES = [
  "/Assets/IMG_2048.jpg",
  "/Assets/IMG_2490 (1).jpg",
  "/Assets/IMG_9897 (1).jpg",
  "/Assets/IMG_2457.jpg",
  "/Assets/03f8d6d2-4f7e-4c97-9992-f9880a8353e9-copied-media~2.jpg",
];

export default function DrunkenLecturesLanding() {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [dropParallax, setDropParallax] = useState(0);
  const [recentPosts, setRecentPosts] = useState([]);
  const dropRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide(i => (i + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (dropRef.current) {
        const rect = dropRef.current.getBoundingClientRect();
        setDropParallax(rect.top * 0.25);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    supabase
      .from('posts')
      .select('id, title, slug, category, poster_url, poster_alt, meta_description, published_at')
      .order('published_at', { ascending: false })
      .limit(3)
      .then(({ data, error }) => {
        if (error) console.error('Supabase fetch error:', error);
        setRecentPosts(data || []);
      });
  }, []);

  const merch = [
    { name: "Tote Bag",   category: "Bags",        price: "KShs 800", image: "/Assets/Product images/tote bags.jpg" },
    { name: "Badges",     category: "Accessories", price: "KShs 250", image: "/Assets/Product images/badges.jpg" },
    { name: "Bucket Hat", category: "Accessories", price: "KShs 500", image: "/Assets/Product images/bucket hat.jpg" },
    { name: "T-shirt",    category: "T Shirts",    price: "KShs 500", image: "/Assets/Product images/t- shirts.jpg" }
  ];

  return (
    <div style={{
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      background: '#FFFFFF',
      minHeight: '100vh',
      margin: 0
    }}>

      <Seo description="Drunken Lectures — talks, panels, and festivals on the ideas that shape how Kenyans live, spend, and think, served up in a bar." path="/" />

      {/* ── HEADER ── */}
      <Navbar />

      {/* ── HERO ── */}
      <section className="hero-section" style={{
        height: '100vh',
        minHeight: '620px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden'
      }}>
        {/* Crossfading background images */}
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={i === 0 ? 'high' : 'low'}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center top',
              opacity: i === activeSlide ? 1 : 0,
              transition: 'opacity 1.4s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 0
            }}
          />
        ))}

        {/* Gradient overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(105deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.25) 100%)',
          zIndex: 1
        }} />

        {/* Slide dots */}
        <div style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '0.5rem',
          zIndex: 3
        }}>
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSlide(i)}
              aria-label={`Slide ${i + 1}`}
              style={{
                width: i === activeSlide ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: i === activeSlide ? '#E8672E' : 'rgba(255,255,255,0.45)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'all 0.4s cubic-bezier(0.22,1,0.36,1)'
              }}
            />
          ))}
        </div>

        {/* Content — left-aligned */}
        <div className="hero-content" style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 clamp(1.5rem, 5vw, 4rem)',
          color: 'white',
          animation: 'fadeInUp 1s ease-out 0.1s both'
        }}>
          <div style={{
            maxWidth: '700px'
          }}>
            <h1 style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
              fontSize: 'clamp(2.8rem, 7.5vw, 6rem)',
              fontWeight: '600',
              lineHeight: '1.06',
              letterSpacing: '-0.02em',
              margin: '0 0 2rem 0'
            }}>
              In the Club...
              <br />
              <span className="accent-underline" style={{ color: '#F0A576', fontFamily: "'Shantell Sans', cursive" }}>
                We all smart
              </span>
            </h1>

            <p style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
              fontSize: 'clamp(1.15rem, 2.6vw, 1.5rem)',
              color: 'rgba(255,255,255,0.8)',
              margin: '0 0 2.25rem',
              lineHeight: 1.6,
            }}>
              Passing bits of knowledge through a glass or two.
            </p>

            <div style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              <a
                href="https://linktr.ee/thedrunkenlectures"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: '#E8672E',
                  color: 'white',
                  padding: '0.9rem 1.9rem',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 28px rgba(232, 103, 46, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 14px 36px rgba(232,103,46,0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(232,103,46,0.4)';
                }}
              >
                <Calendar size={17} />
                Next Lecture
              </a>

              <a
                href="https://drunkenlectures.com/newsletter"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  padding: '0.9rem 1.9rem',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: '700',
                  fontSize: '0.95rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(8px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)';
                }}
              >
                Join the Newsletter
                <ArrowRight size={17} />
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* ── TAGLINE BAND ── */}
      <section style={{
        padding: '4.5rem 1.5rem',
        background: 'white',
        textAlign: 'center',
        borderBottom: '1px solid #EDE6D6'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{
            width: '48px',
            height: '3px',
            background: '#E8672E',
            borderRadius: '2px',
            margin: '0 auto 2rem'
          }} />
          <p style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 'clamp(0.95rem, 2vw, 1.15rem)',
            fontWeight: '700',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#17140F',
            marginBottom: '0.75rem',
            fontStyle: 'italic'
          }}>
            A Community Seeking Knowledge Through a Glass or Two
          </p>
          <p style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 'clamp(0.9rem, 1.8vw, 1.05rem)',
            fontWeight: '400',
            color: '#6B665C',
            fontStyle: 'italic',
            letterSpacing: '0.02em'
          }}>
            Hosted bi-monthly at the beer district in Westlands, Nairobi.
          </p>
        </div>
      </section>

      {/* ── PREVIOUS LECTURES ── */}
      <section id="lectures" style={{
        padding: '6rem 1.5rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div style={{
            fontSize: '0.72rem',
            fontWeight: '700',
            letterSpacing: '0.12em',
            color: '#E8672E',
            marginBottom: '0.9rem',
            textTransform: 'uppercase'
          }}>
            Past Events
          </div>
          <h2 style={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 'clamp(2rem, 5vw, 3.25rem)',
            fontWeight: '600',
            color: '#17140F',
            marginBottom: '1rem',
            letterSpacing: '-0.015em',
            lineHeight: 1.15
          }}>
            Read More of Our<br />
            <span className="accent-underline">Previous Lectures</span>
          </h2>
          <p style={{
            fontSize: '1.05rem',
            color: '#6B665C',
            maxWidth: '480px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Dive deep into Kenya&apos;s most thought-provoking discussions
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem'
        }}>
          {recentPosts.map((post, index) => (
            <article
              key={post.id}
              onClick={() => navigate(`/lectures/${post.slug}`)}
              style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.06)',
                transition: 'all 0.45s cubic-bezier(0.22,1,0.36,1)',
                cursor: 'pointer',
                animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 18px 44px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
              }}
            >
              <div style={{ height: '230px', position: 'relative', overflow: 'hidden' }}>
                {post.poster_url && (
                  <img
                    loading="lazy"
                    decoding="async"
                    src={post.poster_url}
                    alt={post.poster_alt || post.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                  />
                )}
                {post.category && (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    background: '#FBE4D2',
                    padding: '0.3rem 0.8rem',
                    borderRadius: '5px',
                    fontSize: '0.7rem',
                    fontWeight: '800',
                    color: '#8A3D15',
                    letterSpacing: '0.07em'
                  }}>
                    {post.category.toUpperCase()}
                  </div>
                )}
              </div>

              <div style={{ padding: '1.6rem' }}>
                <div style={{
                  fontSize: '0.78rem',
                  color: '#E8672E',
                  fontWeight: '700',
                  marginBottom: '0.6rem',
                  letterSpacing: '0.06em'
                }}>
                  {post.published_at
                    ? new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()
                    : ''}
                </div>

                <h3 style={{
                  fontSize: '1.15rem',
                  fontWeight: '800',
                  color: '#17140F',
                  marginBottom: '0.7rem',
                  lineHeight: '1.35',
                  letterSpacing: '-0.01em'
                }}>
                  {post.title}
                </h3>

                <p style={{
                  fontSize: '0.9rem',
                  color: '#6B665C',
                  lineHeight: '1.65',
                  marginBottom: '1.25rem'
                }}>
                  {post.meta_description}
                </p>

                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  color: '#E8672E',
                  fontWeight: '700',
                  fontSize: '0.88rem'
                }}>
                  Read More <ChevronRight size={15} />
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── MERCH SECTION ── */}
      <section id="merch" style={{
        padding: '7rem 1.5rem',
        background: '#F3EEE1'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
            <div style={{
              fontSize: '0.72rem',
              fontWeight: '700',
              letterSpacing: '0.14em',
              color: '#E8672E',
              marginBottom: '1rem',
              textTransform: 'uppercase',
              fontFamily: "'Inter', sans-serif"
            }}>
              The Drop
            </div>
            <h2 style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
              fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
              fontWeight: '600',
              color: '#17140F',
              marginBottom: '1rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.1
            }}>
              Shop Our Merch &<br />
              <span className="accent-underline">Accessories</span>
            </h2>
            <p style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '1.1rem',
              fontWeight: '400',
              color: '#6B665C',
              maxWidth: '400px',
              margin: '0 auto',
              lineHeight: 1.6
            }}>
              Wear the culture. Rep the movement.
            </p>
          </div>

          <div className="merch-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1.25rem'
          }}>
            {merch.map((item, index) => (
              <a
                key={index}
                href="/merch"
                style={{
                  background: 'white',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                  transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s cubic-bezier(0.22,1,0.36,1)',
                  cursor: 'pointer',
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                  textDecoration: 'none',
                  display: 'block',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.04)';
                  e.currentTarget.style.boxShadow = '0 24px 60px rgba(0,0,0,0.14)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.07)';
                }}
              >
                <div style={{
                  height: '320px',
                  overflow: 'hidden',
                  borderRadius: '24px 24px 0 0'
                }}>
                  <img
                    loading="lazy"
                    decoding="async"
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                </div>
                <div style={{ padding: '1.5rem 1.75rem 1.75rem' }}>
                  <h3 style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '1.3rem',
                    fontWeight: '700',
                    color: '#17140F',
                    marginBottom: '0.3rem',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2
                  }}>
                    {item.name}
                  </h3>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '0.85rem',
                    fontWeight: '400',
                    color: '#8A8577',
                    marginBottom: '0.75rem',
                    letterSpacing: '0.01em'
                  }}>
                    {item.category}
                  </p>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    color: '#17140F'
                  }}>
                    {item.price}
                  </p>
                </div>
              </a>
            ))}
          </div>

        </div>
      </section>

      {/* ── DRUNKEN LECTURES DROP ── */}
      <section
        ref={dropRef}
        className="drop-section"
        style={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: '520px',
          display: 'flex',
          alignItems: 'center',
          background: '#1C3A56',
        }}
      >
        {/* Parallax background image */}
        <img
          src="/Assets/Drunken Lectures drop.PNG"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="drop-bg-img"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '150%',
            objectFit: 'cover',
            objectPosition: 'center top',
            transform: `translateY(calc(-25% + ${dropParallax}px))`,
            willChange: 'transform',
          }}
        />

        {/* Gradient overlay — full cover on mobile, left-fade on desktop */}
        <div className="drop-overlay" style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
        }} />

        {/* Text content */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: 'clamp(3rem, 8vw, 5rem) clamp(1.5rem, 5vw, 4rem)',
        }}>
          <div style={{ maxWidth: '500px', color: 'white' }}>
            <p style={{
              fontSize: '0.75rem',
              fontWeight: '700',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              margin: '0 0 1rem',
              opacity: 0.75,
            }}>
              Limited Time Offer
            </p>
            <h2 style={{
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif",
              fontSize: 'clamp(2.2rem, 8vw, 4rem)',
              fontWeight: '600',
              lineHeight: 1.05,
              letterSpacing: '-0.02em',
              margin: '0 0 1.25rem',
            }}>
              The Drunken<br />Lectures Drop
            </h2>
            <p style={{
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              lineHeight: 1.65,
              opacity: 0.85,
              margin: '0 0 1.25rem',
              maxWidth: '420px',
            }}>
              A limited release from Drunken Lectures, created for those who appreciate good drinks, good stories, and unforgettable nights. Once it&apos;s gone, it&apos;s gone.
            </p>
            <p style={{
              fontSize: '0.95rem',
              fontWeight: '700',
              margin: '0 0 2rem',
            }}>
              Buy our limited released merch
            </p>
            <a
              href="/merch"
              style={{
                display: 'inline-block',
                background: 'white',
                color: '#17140F',
                padding: '0.9rem 2.25rem',
                fontSize: '0.8rem',
                fontWeight: '700',
                letterSpacing: '0.1em',
                textDecoration: 'none',
                textTransform: 'uppercase',
                borderRadius: '4px',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E8672E';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#17140F';
              }}
            >
              Shop the Drop
            </a>
          </div>
        </div>
      </section>

      {/* ── TRUST INDICATORS ── */}
      <section style={{
        padding: '5rem 1.5rem',
        background: '#FFFFFF',
        borderBottom: '1px solid #EDE6D6'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '3rem',
          textAlign: 'center'
        }}>
          {[
            {
              icon: 'Assets/globe-free-img.png',
              title: 'Nationwide Shipping',
              desc: 'Fast and reliable delivery across the country. Wherever you are, your order gets to you safely and on time.'
            },
            {
              icon: 'Assets/quality-free-img.png',
              title: 'Best Quality',
              desc: 'Carefully selected products made with premium materials to ensure exceptional quality and lasting value.'
            },
            {
              icon: 'Assets/tag-free-img.png',
              title: 'Best Offers',
              desc: 'Enjoy competitive prices, exclusive deals, and special discounts on our most popular items.'
            },
            {
              icon: 'Assets/lock-free-img.png',
              title: 'Secure Payments',
              desc: 'Shop with confidence using trusted and encrypted payment methods that keep your information safe.'
            }
          ].map((item, index) => (
            <div key={index} style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both` }}>
              <img
                loading="lazy"
                decoding="async"
                src={item.icon}
                alt={item.title}
                style={{ width: '56px', height: '56px', objectFit: 'contain', marginBottom: '1.25rem' }}
              />
              <h3 style={{
                fontSize: '1.05rem',
                fontWeight: '700',
                color: '#17140F',
                marginBottom: '0.5rem'
              }}>
                {item.title}
              </h3>
              <p style={{
                fontSize: '0.9rem',
                color: '#6B665C',
                lineHeight: '1.6',
                maxWidth: '220px',
                margin: '0 auto'
              }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <Footer />

      {/* ── GLOBAL STYLES ── */}
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (min-width: 900px) {
          .desktop-nav { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
          .drop-overlay {
            background: linear-gradient(90deg, rgba(15,26,38,0.8) 0%, rgba(15,26,38,0.5) 35%, rgba(15,26,38,0.12) 60%, rgba(15,26,38,0.02) 100%);
          }
        }
        @media (max-width: 899px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
          .drop-section { min-height: unset !important; }
          .drop-bg-img {
            height: 100% !important;
            transform: none !important;
            object-position: 65% center !important;
          }
          .drop-overlay {
            background: linear-gradient(
              to bottom,
              rgba(15,26,38,0.3) 0%,
              rgba(15,26,38,0.5) 45%,
              rgba(15,26,38,0.75) 100%
            );
          }
        }
        @media (max-width: 1024px) {
          #merch .merch-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          #merch .merch-grid { grid-template-columns: 1fr !important; }
          .hero-section { align-items: flex-end !important; }
          .hero-content { padding-bottom: 5.5rem !important; }
        }

        a:focus-visible, button:focus-visible {
          outline: 2px solid #E8672E;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
}
